import {formatTime, useAsyncEffect, mergeData} from "@/helper/method";
import {PlanBoardBO, PlanBoardVO, useQueryPlanBoard} from "@/service/plan.s";
import React, {useMemo, useState} from "react";
import {useAllDict} from "@/store/dict.c";
import {PlanPO} from "@/model/mission";
import {Avatar, Box, Card, Grid, CircularProgress, LinearProgress, Paper, Typography} from "@mui/material";
import Stack from "@mui/material/Stack";
import {MoreVertOutlined, QueryBuilderOutlined} from "@mui/icons-material";
import CardContent from "@mui/material/CardContent";
import {DictPO} from "@/model/system";
import Chip from "@mui/material/Chip";
import {AddOutlined} from "@mui/icons-material";
import InfiniteScroll from "react-infinite-scroll-component";
import {styled} from "@mui/material/styles";
import {drawerClasses} from "@mui/material/Drawer";

const planBoardColors = ["#e7e2f3", "#e1f5fa", "#fff4e5", "#d2f9f4"]


const ScrollBoardWrapper = styled(Box)(() => ({
    paddingRight: '5px',
    maxHeight: "calc(100vh - 266px)",
    display: "flex",
    flexDirection: "column",
    minHeight: 100,
    overflowY: "scroll",
    "::-webkit-scrollbar": {
        width: 7,
        height: 7,
    },
    "::-webkit-scrollbar-thumb": {
        [`& .${drawerClasses.paper}`]: {
            backgroundColor: "background.paper",
        },
        cursor: "pointer",
        borderRadius: "3px",
    },
    "&:hover::-webkit-scrollbar-thumb": {
        backgroundColor: "rgba(231,231,226)",
    },
}));

export interface PlanBoardProps {
    onSearch: (query: string) => void;
}

export const PlanBoard = () => {
    const {mutateAsync: queryPlanBoardMutate} = useQueryPlanBoard()
    const [planBoardMap, setPlanBoardMap] = useState<PlanBoardVO>({})
    const {planStatus} = useAllDict()

    useAsyncEffect(async () => {
        await loadData({current: 1, statusId: 0})
    }, [])

    const loadData = async (params: Omit<PlanBoardBO, "size">) => {
        const result = await queryPlanBoardMutate({...params, size: 10})
        if (result.code == 0) {
            const statusId = params.statusId
            if (statusId > 0) {
                setPlanBoardMap({
                    ...planBoardMap,
                    [statusId]: mergeData<PlanPO>(
                        planBoardMap[statusId]?.list || [],
                        result.data![statusId],
                    )
                })
            } else {
                setPlanBoardMap(result.data!)
            }
        }
    }

    const DraggableCard = ({planBoard, index}: { planBoard: PlanPO, index: number }) => {
        return (
            <Card variant={'outlined'} sx={{
                border: 'none',
                borderRadius: '1rem',
                padding: "12px 14px",
                marginBottom: "12px",
                cursor: "pointer"
            }}>
                <Stack mb={1} direction={'row'} justifyContent="space-between" alignItems="center">
                    <Typography variant='subtitle1' textOverflow={'ellipsis'} overflow={'hidden'} fontWeight={600} whiteSpace={'nowrap'}
                                fontSize={12}>{planBoard.planName}</Typography>
                    <MoreVertOutlined fontSize={'small'} color={'action'}/>
                    {/*<Chip*/}
                    {/*    size="small"*/}
                    {/*    label={task.priority}*/}
                    {/*    color={task.priority === "high" ? "error" : "default"}*/}
                    {/*/>*/}
                </Stack>
                {/*<Stack direction={'row'} spacing={1} mb={1}>*/}
                {/*    <Chip size={'small'} variant={'outlined'} color={'warning'} label={'较高'}/>*/}
                {/*    <Chip size={'small'} variant={'outlined'} color={'success'} label={'子任务'}/>*/}
                {/*</Stack>*/}
                <Typography variant="body2" color="text.secondary" fontSize={11} mb={2}>
                    {planBoard.description}
                </Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Stack alignItems={'center'} columnGap={1} direction={'row'}>
                        <Avatar sx={{width: 24, height: 24}} src={planBoard.executor}/>
                        <Typography variant={'subtitle2'} color={'textSecondary'}>{planBoard.executor}</Typography>
                    </Stack>
                </Box>
                <Box mt={1}>
                    <LinearProgress sx={{height: 4}} variant="determinate" value={planBoard.ratio}/>
                </Box>
                <Stack mt={1} direction={'row'} alignContent={'center'} alignItems={'center'} spacing={0.3}>
                    <QueryBuilderOutlined fontSize={'inherit'} color={'disabled'}/> <Stack direction={'row'} alignContent={'center'} spacing={0.2} alignItems={'center'}>
                    <Typography variant={'caption'} color={'textSecondary'}>
                        {formatTime("D", planBoard.startTime)} 至 {formatTime("D", planBoard.endTime)}
                    </Typography>
                </Stack>
                </Stack>
            </Card>
        );
    };

    const DroppableColumn = ({columnIndex, statusId}: { columnIndex: number, statusId: number }) => {
        const currentPlanBoard = planBoardMap[statusId]
        const hasMore = currentPlanBoard?.current < Math.ceil(currentPlanBoard?.total / currentPlanBoard?.size);
        return (<ScrollBoardWrapper id={`scrollable${columnIndex}`} key={columnIndex}>
                <InfiniteScroll dataLength={currentPlanBoard?.total || 0} next={() => {
                    loadData({current: currentPlanBoard?.current + 1, statusId})
                }} hasMore={hasMore} scrollableTarget={`scrollable${columnIndex}`} loader={<Box>加载中</Box>}>
                    {
                        currentPlanBoard?.list?.map((planBoard: PlanPO, index: number) => (
                            <div style={{height: 100}}>{planBoard.planName} </div>
                        ))
                    }
                </InfiniteScroll>
            </ScrollBoardWrapper>
        );
    };


    const planColumn = useMemo(() => {
        return planStatus?.sort((a, b) => a.sort! - b.sort!)
    }, [planStatus]);

    return <CardContent>
        <Grid container spacing={3}>
            {planColumn?.map((dict: DictPO, index: number) => {
                const currentPlanBoard = planBoardMap[dict.value]
                const hasMore = currentPlanBoard?.current < Math.ceil(currentPlanBoard?.total / currentPlanBoard?.size);
                return (
                    <Grid size={3} key={index}>
                        <Paper sx={{py: 2, pl: '12px', backgroundColor: planBoardColors[index], borderRadius: '1.2rem'}}>
                            <Stack pr={'12px'} direction={'row'} mb={2} justifyContent={'space-between'}>
                                <Stack spacing={1} direction={'row'}>
                                    <Typography variant="h6" fontSize={14}>{dict.label}</Typography>
                                    <Chip color={'error'} variant={'outlined'} label={planBoardMap[dict.value]?.total || 0}/>
                                </Stack>
                                <AddOutlined fontSize={'small'} sx={{bgcolor: '#fff', borderRadius: '50%'}} color={'action'}/>
                            </Stack>
                            <ScrollBoardWrapper id={`scrollable${index}`} key={index}>
                                <InfiniteScroll dataLength={currentPlanBoard?.total || 0} next={async () => {
                                    await loadData({current: currentPlanBoard?.current + 1, statusId: dict.value})
                                }} hasMore={hasMore} scrollableTarget={`scrollable${index}`}
                                                loader={<Box textAlign={'center'}><CircularProgress size={'20px'} color='secondary'/></Box>}>
                                    {
                                        currentPlanBoard?.list?.map((planBoard: PlanPO, index: number) => (
                                            <DraggableCard planBoard={planBoard} index={index} key={planBoard.id}/>
                                        ))
                                    }
                                </InfiniteScroll>
                            </ScrollBoardWrapper>
                        </Paper>
                    </Grid>
                )
            })}
        </Grid>
    </CardContent>
}